import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter from "../../../components/LHFilter";
import LHDataTable from "../../../components/LHDataTable";
import { FilterItem } from "../../../components/LHFilter";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

// Helper to format period/week display
const formatPeriodWeek = (item: any): string => {
  const period = item?.period ?? "";
  const week = item?.week ?? "";
  if (period && week) return `P${period}W${week}`;
  if (period) return `P${period}`;
  if (week) return `W${week}`;
  return "-";
};

interface LeadershipWalkMetricsProps {
  filterOptions: FilterItem[];
  handleFilterChange: (key: string, value: any) => void;
  isLoading: boolean;
  apiError: string | null;
  leadershipWalkData?: any[];
  tableColumns: any[];
  handleRowClick: (rowData: any) => void;
  tableData: any[];
  isMobile: boolean;
  // applyFilters is handled by parent auto-apply; no direct use here
  // applyFilters: () => Promise<void>;
  resetFilters?: () => Promise<void>;
  // Mobile filter props
  showMobileFilterButton?: boolean;
  onMobileFilterPress?: () => void;
}

const LeadershipWalkMetrics: React.FC<LeadershipWalkMetricsProps> = ({
  filterOptions,
  handleFilterChange,
  isLoading,
  apiError,
  tableColumns,
  handleRowClick,
  tableData,
  resetFilters,
  showMobileFilterButton = false,
  onMobileFilterPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const customizedColumns = React.useMemo(() => {
    const hasPeriodData = tableData.some(
      (item) => "period" in item && item.period !== undefined
    );
    const hasWeekData = tableData.some(
      (item) => "week" in item && item.week !== undefined
    );

    const hasPeriod = tableColumns.some((col) => col.columnKey === "period");
    const hasWeek = tableColumns.some((col) => col.columnKey === "week");
    const hasCombined = tableColumns.some(
      (col) => col.columnKey === "periodWeek"
    );
    const shouldCombine = hasPeriod && hasWeek && !hasCombined;

    let combinedCreated = false;
    let result = [...tableColumns];

    if (
      !hasPeriod &&
      !hasWeek &&
      !hasCombined &&
      (hasPeriodData || hasWeekData)
    ) {
      result = [
        ...result.slice(0, 1),
        {
          columnKey: "periodWeek",
          header: "Period Week",
          getSortValue: (item: any) => {
            const p = Number(item.period) || 0;
            const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
            return p * 100 + w;
          },
          renderCell: (item: any) => (
              <Text
                style={[
                  styles.cellText,
                  { color: isDarkMode ? colors.text.inverse : "rgb(97, 97, 97)" },
                ]}
              >
                {formatPeriodWeek(item)}
              </Text>
          ),
        },
        ...result.slice(1),
      ];
    }

    return result
      .map((column) => {
        if (column.columnKey === "details") {
          return {
            ...column,
            renderCell: (item: any) => (
              <Text
                style={[
                  styles.detailsCell,
                  { color: isDarkMode ? colors.text.inverse : "#005CBC" },
                ]}
                onPress={() =>
                  handleRowClick({ ...item, columnKey: "details" })
                }
              >
                {item.details || "-"}
              </Text>
            ),
          };
        }

        if (column.columnKey === "periodWeek") {
          return {
            ...column,
            getSortValue: (item: any) => {
              const p = Number(item.period) || 0;
              const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
              return p * 100 + w;
            },
            renderCell: (item: any) => (
              <Text
                style={[
                  styles.cellText,
                  { color: isDarkMode ? colors.text.inverse : "rgb(97, 97, 97)" },
                ]}
              >
                {formatPeriodWeek(item)}
              </Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "period") {
          combinedCreated = true;
          return {
            ...column,
            columnKey: "periodWeek",
            header: "Period Week",
            getSortValue: (item: any) => {
              const p = Number(item.period) || 0;
              const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
              return p * 100 + w;
            },
            renderCell: (item: any) => (
              <Text
                style={[
                  styles.cellText,
                  { color: isDarkMode ? colors.text.inverse : "rgb(97, 97, 97)" },
                ]}
              >
                {formatPeriodWeek(item)}
              </Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "week" && combinedCreated) {
          return null;
        }

        return column;
      })
      .filter((col) => col !== null);
  }, [tableColumns, tableData, handleRowClick, isDarkMode, colors.text.inverse]);

  // Compute iOS mobile boolean inside the component to avoid module-level reference issues
  const windowWidth = Dimensions.get("window").width;
  const isMobileIos = Platform.OS === "ios" && windowWidth < 768;

  // Pagination state for iOS mobile card view
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // always show 10 records per mobile page as requested
  const [displayStart, setDisplayStart] = useState(1); // window start for the two-number cells

  useEffect(() => {
    // Reset to first page whenever the table data changes
    setCurrentPage(1);
    setDisplayStart(1);
  }, [tableData]);

  // Ensure currentPage does not exceed total pages when data shrinks
  const totalPages = Math.max(
    1,
    Math.ceil((tableData?.length || 0) / rowsPerPage)
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = isMobileIos
    ? tableData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : tableData;

  return (
    <View
      style={[
        styles.metricsWrapper,
        { backgroundColor: colors.background.page },
      ]}
    >
      <View
        style={[
          styles.dataTableContainer,
          isMobileIos && styles.dataTableContainerMobile,
          isDarkMode && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
        ]}
      >
        <View
          style={[
            styles.headerBox,
            isMobileIos && styles.headerBoxMobileSpacing,
            isMobileIos && styles.headerBoxIOSMobile,
          ]}
        >
          <View style={styles.headerTitleContainer}>
            <Text
              style={[
                styles.headerTitle,
                isMobileIos && styles.headerTitleMobile,
                { color: isDarkMode ? colors.text.inverse : "#3A3A3A" },
              ]}
              numberOfLines={isMobileIos ? 2 : undefined}
              ellipsizeMode={isMobileIos ? "tail" : undefined}
            >
              Leadership Walk List
            </Text>
          </View>
          {showMobileFilterButton && isMobileIos && (
            <TouchableOpacity
              style={styles.mobileFilterButton}
              onPress={onMobileFilterPress}
            >
              <MaterialIcons name="filter-list" size={20} color={colors.text.inverse} />
              <Text style={styles.mobileFilterButtonText}>Filter</Text>
            </TouchableOpacity>
          )}
        </View>
        {/* Hide regular filters when mobile filter button is shown */}
        {!showMobileFilterButton && (
          <View style={styles.filterContainer}>
            {/* Apply filters immediately on selection change (no Apply button) */}
            <LHFilter
              filters={filterOptions}
              onFilterChange={(key: string, value: any) => {
                // call parent handler to update local filter state
                // actual application of the filters is handled by the parent (auto-apply)
                handleFilterChange(key, value);
              }}
              itemsPerRow={getResponsiveItemsPerRow(4)}
              onClearAllFilters={resetFilters}
            />
          </View>
        )}

        {isLoading && (
          <View style={styles.messageContainer}>
            <Text
              style={[
                styles.loadingText,
                { color: isDarkMode ? colors.text.inverse : "#0066CC" },
              ]}
            >
              Loading leadership walk data...
            </Text>
          </View>
        )}

        {apiError && (
          <View style={styles.messageContainer}>
            <Text
              style={[
                styles.errorText,
                { color: isDarkMode ? colors.text.inverse : "#DC3545" },
              ]}
            >
              Error: {apiError}
            </Text>
          </View>
        )}

        {!isLoading && (
          <>
            {tableData.length === 0 ? (
              <View style={styles.messageContainer}>
                <Text style={{ color: isDarkMode ? colors.text.inverse : colors.text.primary }}>No records found</Text>
              </View>
            ) : isMobileIos ? (
              // Card layout for iOS mobile
              <View style={styles.cardsContainer}>
                {paginatedData.map((item, index) => (
                  <React.Fragment key={`card-${item.id || index}`}>
                    <CardItem
                      item={item}
                      columns={customizedColumns}
                      onRowClick={handleRowClick}
                      isDarkMode={isDarkMode}
                      colors={colors}
                    />
                    {index < paginatedData.length - 1 && <CardSeparator />}
                  </React.Fragment>
                ))}

                {/* Mobile pagination controls (use same UX as LHDataTable mobile pagination) */}
                <View style={styles.mobilePaginationContainer}>
                  <View style={styles.mobilePaginationBox}>
                    <TouchableOpacity
                      disabled={displayStart === 1 && currentPage === 1}
                      onPress={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        // move window so it starts at the new page
                        setDisplayStart(Math.max(1, newPage));
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart === 1 &&
                          currentPage === 1 &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-prev-page-button"
                      accessibilityLabel="Previous page"
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={16}
                        color={
                          displayStart === 1 && currentPage === 1
                            ? (isDarkMode ? colors.text.secondary : "#A0A0A0")
                            : colors.brand.primary
                        }
                      />
                    </TouchableOpacity>

                    {/* Two-number window: displayStart and displayStart+1 (cap by totalPages). For single page, show only current page in first cell and empty second cell */}
                    {(() => {
                      const a = displayStart;
                      const b = Math.min(totalPages, displayStart + 1);
                      const aIsActive = currentPage === a;
                      const bIsActive = currentPage === b;
                      const isSinglePage = totalPages === 1;

                      return (
                        <>
                          <TouchableOpacity
                            disabled={a > totalPages}
                            onPress={() => a <= totalPages && setCurrentPage(a)}
                            accessibilityLabel={`Go to page ${a}`}
                            style={[
                              styles.mobilePaginationCell,
                              aIsActive
                                ? styles.mobilePaginationCurrentCell
                                : styles.mobilePaginationNextCell,
                              aIsActive
                                ? { backgroundColor: colors.brand.primary }
                                : {
                                    backgroundColor: isDarkMode
                                      ? colors.background.elevated
                                      : "#FBFCFE",
                                  },
                            ]}
                          >
                            <Text
                              style={[
                                aIsActive
                                  ? styles.mobilePaginationCurrentText
                                  : styles.mobilePaginationNextText,
                                !aIsActive && {
                                  color: isDarkMode
                                    ? colors.text.inverse
                                    : colors.brand.primary,
                                },
                              ]}
                            >
                              {a}
                            </Text>
                          </TouchableOpacity>

                          {/* For single page, show empty cell instead of page 2 */}
                          {isSinglePage ? (
                            <View
                              style={[
                                styles.mobilePaginationCell,
                                styles.mobilePaginationEmptyCell,
                              ]}
                            />
                          ) : (
                            <TouchableOpacity
                              disabled={b > totalPages}
                              onPress={() =>
                                b <= totalPages && setCurrentPage(b)
                              }
                              accessibilityLabel={`Go to page ${b}`}
                              style={[
                                styles.mobilePaginationCell,
                                bIsActive
                                  ? styles.mobilePaginationCurrentCell
                                  : styles.mobilePaginationNextCell,
                                bIsActive
                                  ? { backgroundColor: colors.brand.primary }
                                  : {
                                      backgroundColor: isDarkMode
                                        ? colors.background.elevated
                                        : "#FBFCFE",
                                    },
                              ]}
                            >
                              <Text
                                style={[
                                  bIsActive
                                    ? styles.mobilePaginationCurrentText
                                    : styles.mobilePaginationNextText,
                                  !bIsActive && {
                                    color: isDarkMode
                                      ? colors.text.inverse
                                      : colors.brand.primary,
                                  },
                                ]}
                              >
                                {b}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </>
                      );
                    })()}

                    <TouchableOpacity
                      disabled={
                        displayStart + 1 >= totalPages &&
                        currentPage === totalPages
                      }
                      onPress={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        setDisplayStart(newPage);
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart + 1 >= totalPages &&
                          currentPage === totalPages &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-next-page-button"
                      accessibilityLabel="Next page"
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={16}
                        color={
                          displayStart + 1 >= totalPages &&
                          currentPage === totalPages
                            ? (isDarkMode ? colors.text.secondary : "#A0A0A0")
                            : colors.brand.primary
                        }
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ) : (
              // Table layout for web and iPad
              <LHDataTable
                data={tableData}
                columns={customizedColumns}
                tableHeader=""
                tableDescription={`Showing ${tableData.length} result(s)`}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  metricsWrapper: {
    padding: 0,
    backgroundColor: "#F5F9FF",
    flex: 1,
  },
  headerBox: {
    backgroundColor: "transparent",
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitleContainer: {
    flex: 1,
    marginRight: 12,
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: FontFamily.bold,
    fontWeight: "700", // Bold
    fontStyle: "normal",
    fontSize: 24, // Font Size/H5 (approx)
    lineHeight: 28, // Line Height/H5 (approx)
    letterSpacing: 0,
    color: "#3A3A3A",
    textAlign: "left",
    marginBottom: 8,
  },
  headerTitleMobile: {
   // marginBottom: 6,
    marginTop: -20,
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  filterContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
    fontFamily: FontFamily.regular,
  },
  dataTableContainer: {
    // default (desktop / web / iPad) values — mobile overrides are applied at render time
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: "1%",
    padding: 24,
  },
  dataTableContainerMobile: {
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
  },
  // iOS mobile-only spacing override for header
  headerBoxMobileSpacing: {
    marginBottom: 0,
  },
  // iOS mobile-only layout for filter button on right
  headerBoxIOSMobile: {
    justifyContent: "flex-end",
  },
  cellText: {
    fontSize: 14,
    color: "rgb(97, 97, 97)",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    padding: 4,
    fontFamily: FontFamily.regular,
  },
  detailsCell: {
    fontSize: 14,
    color: "#005CBC",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    maxWidth: Platform.OS === "web" ? "100%" : 250,
    fontWeight: 500,
    fontFamily: FontFamily.regular,
  },
  // Card styles for iOS mobile
  cardsContainer: {
    flex: 1,
    backgroundColor: "transparent",
  },
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  cardSeparator: {
    height: 8,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  cardPrimaryValue: {
    flex: 1,
    marginRight: 12,
  },
  cardPrimaryText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  statusTagText: {
    fontSize: 12,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  statusTagCompleted: {
    backgroundColor: "#28A745",
  },
  statusTagInProgress: {
    backgroundColor: "#E5A408",
  },
  statusTagFailed: {
    backgroundColor: "#C12C01",
  },
  statusTagDefault: {
    backgroundColor: "#6C757D",
  },
  cardContent: {
    marginBottom: 8,
  },
  cardRow: {
    marginBottom: 4,
  },
  cardRowText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  cardRowLabel: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  cardRowValue: {
    fontWeight: "400",
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  viewDetailsContainer: {
    paddingVertical: 4,
    marginBottom: -6,
    zIndex: 10, // Ensure it's above the tap overlay
  },
  viewDetailsButton: {
    zIndex: 11, // Higher than container
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
  cardTapOverlay: {
    position: "absolute",
    top: 60, // Start below the header section
    left: 0,
    right: 0,
    bottom: 0, // Will be dynamically adjusted
    backgroundColor: "transparent",
    zIndex: 1, // Higher z-index for reliable touch handling on iOS
  },
  // Mobile pagination styles (copied from LHDataTable)
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationEmptyCell: {
    backgroundColor: "#FBFCFE",
    opacity: 0.3,
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#034895",
    borderColor: "#034895",
    borderRadius: 24,
    paddingHorizontal: 16,
    height: 32,
    width: 120,
    minWidth: 60,
    gap: 4,
    position: "relative",
    top: -12,
    zIndex: 2,
    justifyContent: "center",
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
});

const getResponsiveItemsPerRow = (itemsPerRow: number): number => {
  const windowWidth = Dimensions.get("window").width;

  if (Platform.OS === "web") {
    return windowWidth > 1200
      ? Math.min(itemsPerRow, 4)
      : Math.min(itemsPerRow, 3);
  } else if (Platform.OS === "ios" || Platform.OS === "android") {
    if (windowWidth > 768) {
      return Math.min(itemsPerRow, 3);
    }
    return Math.min(itemsPerRow, 2);
  }
  return itemsPerRow;
};

// Card separator component
const CardSeparator = () => <View style={{ height: 8 }} />;

// Card component for iOS mobile
interface CardItemProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any) => void;
  isDarkMode: boolean;
  colors: any;
}

const CardItem: React.FC<CardItemProps> = ({ item, columns, onRowClick, isDarkMode, colors }) => {

  // Get the status tag style based on status value. Accepts strings or React elements.
  const getStatusTagStyle = (status: any) => {
    // Extract text from potential React element like <Text>In Progress</Text>
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") {
        raw = children;
      } else if (Array.isArray(children)) {
        raw = children.join("");
      } else {
        raw = String(children);
      }
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "completed") {
      return styles.statusTagCompleted;
    }
    if (normalized.includes("inprogress")) {
      return styles.statusTagInProgress;
    }
    if (normalized === "failed") {
      return styles.statusTagFailed;
    }
    return styles.statusTagDefault;
  };

  // Return explicit background color for a status value to avoid platform style merging issues
  const getStatusTagColor = (status: any) => {
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") raw = children;
      else if (Array.isArray(children)) raw = children.join("");
      else raw = String(children);
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");
    if (normalized === "completed") return "#027C06";
    if (normalized.includes("inprogress")) return "#E5A408";
    if (normalized === "failed") return "#C12C01";
    return isDarkMode ? colors.background.elevated : "#6C757D";
  };

  // Get the status text color
  const getStatusTextColor = (_status: any) => {
    // all statuses currently use white for text
    return "#FFFFFF";
  };

  // First column (zone) - should be clickable for navigation on iOS mobile
  const firstColumn = columns[0];
  const firstValue = firstColumn?.renderCell
    ? firstColumn.renderCell(item)
    : item[firstColumn?.columnKey];

  // Last column (status) - non-clickable
  const lastColumn = columns[columns.length - 1];
  let lastValue: any = item[lastColumn?.columnKey];
  if (lastValue === undefined || lastValue === null) {
    lastValue = "new";
  }

  // All columns for display (skip first and last columns)
  const displayColumns = columns.slice(1, columns.length - 1);

  return (
    <View
      style={[
        styles.cardContainer,
        isDarkMode && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
          borderWidth: 1,
        },
      ]}
    >
      {/* First Row: Primary value (left) + Status tag (right) */}
      <View style={styles.cardHeader}>
        <TouchableOpacity
          onPress={() => {
            // For zone column, pass columnKey as 'zone' for navigation
            const clickData =
              firstColumn?.columnKey === "zone"
                ? { ...item, columnKey: "zone" }
                : { ...item, columnKey: "details" };
            onRowClick(clickData);
          }}
          style={styles.cardPrimaryValue}
        >
          <Text
            style={[
              styles.cardPrimaryText,
              { color: isDarkMode ? colors.brand.primary : "#005CBC" },
            ]}
            numberOfLines={2}
          >
            {typeof firstValue === "string"
              ? firstValue
              : item[firstColumn?.columnKey]}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => {
            const clickData =
              firstColumn?.columnKey === "zone"
                ? { ...item, columnKey: "zone" }
                : { ...item, columnKey: "details" };
            onRowClick(clickData);
          }}
          style={[
            styles.statusTag,
            getStatusTagStyle(lastValue),
            { backgroundColor: getStatusTagColor(lastValue) },
          ]}
        >
          <Text
            style={[
              styles.statusTagText,
              { color: getStatusTextColor(lastValue) },
            ]}
          >
            {lastValue}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Content Rows: "Header: Value" format - show all fields */}
      <View style={styles.cardContent}>
        {displayColumns.map((column, index) => {
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          // Make zone values clickable in iOS mobile cards
          const isZoneColumn = column.columnKey === "zone";

          return (
            <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
              <Text style={styles.cardRowText}>
                <Text style={[styles.cardRowLabel, isDarkMode && { color: colors.text.inverse }] }>
                  {column.header.toUpperCase()}:{" "}
                </Text>
                {isZoneColumn ? (
                  <TouchableOpacity
                    onPress={() => onRowClick({ ...item, columnKey: "zone" })}
                    style={{ flexShrink: 1 }}
                  >
                    <Text
                      style={[
                        styles.cardRowValue,
                        { color: isDarkMode ? colors.brand.primary : "#005CBC", fontWeight: "500" },
                      ]}
                    >
                      {typeof value === "string" ? value : value || "-"}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={[styles.cardRowValue, isDarkMode && { color: colors.text.inverse }] }>
                    {typeof value === "string" ? value : value || "-"}
                  </Text>
                )}
              </Text>
            </View>
          );
        })}
      </View>

      {/* View Details Link - opens overlay when clicked */}
      {displayColumns.length > 0 && (
        <View style={styles.viewDetailsContainer}>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              onRowClick(item);
            }}
            style={styles.viewDetailsButton}
          >
            <Text style={[styles.viewDetailsText, { color: isDarkMode ? colors.brand.primary : "#005CBC" }]}>View Details</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tap anywhere else overlay for modal - positioned to avoid View Details area */}
      <TouchableOpacity
        style={[
          styles.cardTapOverlay,
          {
            bottom: displayColumns.length > 0 ? 40 : 0, // Leave space for View Details
          },
        ]}
        onPress={() => onRowClick(item)}
        activeOpacity={0.1}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      />
    </View>
  );
};

export default LeadershipWalkMetrics;
